import { Button, useClickOutside } from '@consigli/facade';
import { type ProjectParticipant, type ResponsibleParty } from '@consigli/types';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTimes } from 'react-icons/fa';
import { FiCalendar, FiTag, FiClipboard } from 'react-icons/fi';
import { IoPersonAddOutline } from 'react-icons/io5';

import { BulkAddTopicPopover } from './bulk-add-topic-popover';
import { BulkUpdateDeadlinePopover } from './bulk-update-deadline-popover';
import { BulkUpdateStatusPopover } from './bulk-update-status-popover';
import { ReassignPersonPopover, type ReassignResultItem } from './reassign-person-popover';

interface BulkSelectionBarProps {
  selectedCount: number;
  selectedItemIds: string[];
  participants: ProjectParticipant[];
  customParties?: ResponsibleParty[];
  topics: string[];
  onReassign: (
    itemIds: string[],
    target: {
      toMeetingUserId?: string | null;
      toCustomPartyName?: string | null;
    },
  ) => Promise<void>;
  onUpdateStatus: (itemIds: string[], status: string) => Promise<void>;
  onUpdateDeadline: (itemIds: string[], deadline: string | null) => Promise<void>;
  onAddTopic: (itemIds: string[], topic: string) => Promise<void>;
  onAddCustomTopic?: (topic: string) => void;
  onClearSelection: () => void;
}

export const BulkSelectionBar: FC<BulkSelectionBarProps> = ({
  selectedCount,
  selectedItemIds,
  participants,
  customParties,
  topics,
  onReassign,
  onUpdateStatus,
  onUpdateDeadline,
  onAddTopic,
  onAddCustomTopic,
  onClearSelection,
}) => {
  const { t } = useTranslation();
  const [showReassignDropdown, setShowReassignDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showDeadlineDropdown, setShowDeadlineDropdown] = useState(false);
  const [showTopicDropdown, setShowTopicDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleClickOutside = useCallback(() => {
    if (isLoading) return;
    setShowReassignDropdown(false);
    setShowStatusDropdown(false);
    setShowDeadlineDropdown(false);
    setShowTopicDropdown(false);
  }, [isLoading]);

  useClickOutside({
    ref: containerRef,
    onClickOutside: handleClickOutside,
    enabled:
      showReassignDropdown || showStatusDropdown || showDeadlineDropdown || showTopicDropdown,
  });

  const handleReassign = useCallback(
    async (result: ReassignResultItem[]) => {
      const target = result[0];
      if (!target) return;
      setIsLoading(true);
      try {
        await onReassign(selectedItemIds, {
          toMeetingUserId: target.meetingUserId ?? null,
          toCustomPartyName: target.customPartyName ?? null,
        });
        if (isMountedRef.current) setShowReassignDropdown(false);
      } catch {
        // Error is handled by the parent - keep the picker open for retry
      } finally {
        if (isMountedRef.current) setIsLoading(false);
      }
    },
    [onReassign, selectedItemIds],
  );

  const handleStatusUpdate = useCallback(
    async (status: string) => {
      setIsLoading(true);
      try {
        await onUpdateStatus(selectedItemIds, status);
        if (isMountedRef.current) setShowStatusDropdown(false);
      } catch {
        // Error is handled by the parent - keep the picker open for retry
      } finally {
        if (isMountedRef.current) setIsLoading(false);
      }
    },
    [onUpdateStatus, selectedItemIds],
  );

  const handleDeadlineUpdate = useCallback(
    async (deadline: string | null) => {
      setIsLoading(true);
      try {
        await onUpdateDeadline(selectedItemIds, deadline);
        if (isMountedRef.current) setShowDeadlineDropdown(false);
      } catch {
        // Error is handled by the parent - keep the picker open for retry
      } finally {
        if (isMountedRef.current) setIsLoading(false);
      }
    },
    [onUpdateDeadline, selectedItemIds],
  );

  const handleTopicAdd = useCallback(
    async (topic: string) => {
      setIsLoading(true);
      try {
        await onAddTopic(selectedItemIds, topic);
        if (isMountedRef.current) setShowTopicDropdown(false);
      } catch {
        // Error is handled by the parent - keep the picker open for retry
      } finally {
        if (isMountedRef.current) setIsLoading(false);
      }
    },
    [onAddTopic, selectedItemIds],
  );

  return (
    <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between h-[46px] px-4 bg-brand rounded-t-lg">
      {/* Left side: selected count */}
      <div className="flex items-center gap-3 text-white">
        <div className="flex items-center gap-2">
          <span className="flex items-center justify-center min-w-[28px] h-7 px-2 bg-white text-brand rounded-full text-sm font-bold">
            {selectedCount}
          </span>
          <span className="text-base font-semibold">
            {t('meeting-secretary.action-items.selected')}
          </span>
        </div>
      </div>

      {/* Right side: action buttons and close button */}
      <div className="flex items-center gap-2" ref={containerRef}>
        {/* Reassign Owner */}
        <div className="relative">
          <Button
            ghost
            onClick={() => {
              setShowReassignDropdown((prev) => !prev);
              setShowStatusDropdown(false);
              setShowDeadlineDropdown(false);
              setShowTopicDropdown(false);
            }}
            disabled={isLoading}
            className="!text-white hover:!text-white/80 !text-sm"
          >
            <IoPersonAddOutline className="w-4 h-4" />
            {t('meeting-secretary.action-items.reassign-owner')}
          </Button>

          {showReassignDropdown && (
            <ReassignPersonPopover
              title={t('meeting-secretary.meetings.reassign-to')}
              uppercaseTitle={false}
              participants={participants}
              mode="single"
              allowCustom
              align="right"
              selected={[]}
              customParties={customParties}
              isSaving={isLoading}
              direction="down"
              onSave={handleReassign}
              onClose={() => setShowReassignDropdown(false)}
            />
          )}
        </div>

        <div className="w-px h-6 bg-white/30" />

        {/* Deadline */}
        <div className="relative">
          <Button
            ghost
            onClick={() => {
              setShowDeadlineDropdown((prev) => !prev);
              setShowReassignDropdown(false);
              setShowStatusDropdown(false);
              setShowTopicDropdown(false);
            }}
            disabled={isLoading}
            className="!text-white hover:!text-white/80 !text-sm"
          >
            <FiCalendar className="w-4 h-4" />
            {t('meeting-secretary.action-items.change-deadline')}
          </Button>

          <BulkUpdateDeadlinePopover
            isOpen={showDeadlineDropdown}
            onSelect={handleDeadlineUpdate}
            onClose={() => setShowDeadlineDropdown(false)}
          />
        </div>

        <div className="w-px h-6 bg-white/30" />

        {/* Topic */}
        <div className="relative">
          <Button
            ghost
            onClick={() => {
              setShowTopicDropdown((prev) => !prev);
              setShowReassignDropdown(false);
              setShowStatusDropdown(false);
              setShowDeadlineDropdown(false);
            }}
            disabled={isLoading}
            className="!text-white hover:!text-white/80 !text-sm"
          >
            <FiTag className="w-4 h-4" />
            {t('meeting-secretary.action-items.change-topics')}
          </Button>

          <BulkAddTopicPopover
            isOpen={showTopicDropdown}
            availableTopics={topics}
            onSelect={handleTopicAdd}
            onAddCustomTopic={onAddCustomTopic}
          />
        </div>

        <div className="w-px h-6 bg-white/30" />

        {/* Status */}
        <div className="relative">
          <Button
            ghost
            onClick={() => {
              setShowStatusDropdown((prev) => !prev);
              setShowReassignDropdown(false);
              setShowDeadlineDropdown(false);
              setShowTopicDropdown(false);
            }}
            disabled={isLoading}
            className="!text-white hover:!text-white/80 !text-sm"
          >
            <FiClipboard className="w-4 h-4" />
            {t('meeting-secretary.action-items.update-status')}
          </Button>

          <BulkUpdateStatusPopover isOpen={showStatusDropdown} onSelect={handleStatusUpdate} />
        </div>

        <Button
          ghost
          iconOnly
          icon={FaTimes}
          iconSize={14}
          onClick={onClearSelection}
          aria-label={t('meeting-secretary.action-items.clear-selection')}
          className="!text-white/70 hover:!text-white"
        />
      </div>
    </div>
  );
};
